/**
 * WebSocket protocol shared by the browser client and the race server.
 *
 * Clients only ever send *inputs*; the server owns the simulation and streams
 * snapshots back. That keeps results authoritative and means the same physics
 * code runs on both sides (the client also simulates locally for prediction).
 */

import type { AIDifficulty } from './ai.js';
import type { UpgradeLevels } from './upgrades.js';

export type RaceModeId =
  | 'quick-race'
  | 'championship'
  | 'time-trial'
  | 'practice'
  | 'drift'
  | 'elimination'
  | 'checkpoint'
  | 'online';

export interface LobbySettings {
  trackId: string;
  laps: number;
  mode: RaceModeId;
  aiCount: number;
  aiDifficulty: AIDifficulty;
  isPrivate: boolean;
  maxPlayers: number;
}

export interface LobbyMember {
  id: string;
  name: string;
  carId: string;
  color: string;
  ready: boolean;
  isHost: boolean;
  isAI: boolean;
}

export interface LobbyInfo {
  code: string;
  name: string;
  host: string;
  isPrivate: boolean;
  playerCount: number;
  maxPlayers: number;
  trackId: string;
  laps: number;
  inProgress: boolean;
}

export interface SnapshotCar {
  id: string;
  x: number;
  y: number;
  angle: number;
  vx: number;
  vy: number;
  yawRate: number;
  speed: number;
  gear: number;
  rpm: number;
  slip: number;
  wheelSpin: number;
  impact: number;
  surfaceRear: string;
  lap: number;
  checkpoint: number;
  position: number;
  finished: boolean;
  eliminated: boolean;
}

export interface RaceResultRow {
  id: string;
  name: string;
  carId: string;
  position: number;
  laps: number;
  time: number;
  bestLap: number;
  finished: boolean;
  driftScore: number;
}

export type ClientMessage =
  | { t: 'hello'; name?: string }
  | { t: 'listLobbies' }
  | { t: 'createLobby'; name: string; settings: LobbySettings }
  | { t: 'joinLobby'; code: string }
  | { t: 'leaveLobby' }
  | { t: 'ready'; ready: boolean }
  | { t: 'select'; carId: string; color: string }
  | { t: 'settings'; settings: Partial<LobbySettings> }
  | { t: 'start' }
  | { t: 'chat'; text: string }
  | { t: 'input'; seq: number; throttle: number; brake: number; steer: number; handbrake: boolean; boost: boolean }
  /**
   * Asks the server to put this player's car back on the racing line. The server
   * owns the car, so a client-side recovery would be overwritten immediately.
   */
  | { t: 'reset' }
  | { t: 'ping'; time: number }
  | { t: 'loaded' };

export type ServerMessage =
  | { t: 'welcome'; playerId: string; name: string; serverVersion: string }
  | { t: 'lobbies'; lobbies: LobbyInfo[] }
  | { t: 'lobby'; code: string; name: string; settings: LobbySettings; members: LobbyMember[]; hostId: string; inProgress: boolean }
  | { t: 'left' }
  | { t: 'joined'; code: string; playerId: string }
  | { t: 'start'; trackId: string; laps: number; mode: RaceModeId; countdown: number; seed: number; startAt: number }
  | { t: 'snapshot'; tick: number; time: number; serverTime: number; cars: SnapshotCar[] }
  | { t: 'lap'; playerId: string; lap: number; lapTime: number; bestLap: number; valid: boolean; position: number }
  | { t: 'countdown'; value: number }
  | { t: 'position'; positions: Array<{ id: string; position: number }> }
  | { t: 'results'; rows: RaceResultRow[]; yourId: string }
  | { t: 'raw'; trackId: string; laps: number; mode: RaceModeId }
  | { t: 'pong'; time: number; serverTime: number }
  | { t: 'chat'; from: string; text: string; system?: boolean }
  | { t: 'error'; message: string; code?: string };

export const NET = {
  /** Server simulation rate. */
  tickRate: 60,
  /** Snapshot broadcast rate. */
  snapshotRate: 20,
  /** Fixed server timestep. */
  dt: 1 / 60,
  protocolVersion: 1,
};

export function encodeMessage(msg: ClientMessage | ServerMessage): string {
  return JSON.stringify(msg);
}

export function decodeMessage<T = ClientMessage | ServerMessage>(raw: string): T | null {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof (parsed as { t?: unknown }).t === 'string') {
      return parsed as T;
    }
    return null;
  } catch {
    return null;
  }
}

/** Runtime validation for the input frames a client sends to the server. */
export function validateInput(msg: Extract<ClientMessage, { t: 'input' }>): boolean {
  const okNum = (v: unknown, lo: number, hi: number): boolean => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
  return (
    Number.isInteger(msg.seq) &&
    msg.seq >= 0 &&
    okNum(msg.throttle, 0, 1) &&
    okNum(msg.brake, 0, 1) &&
    okNum(msg.steer, -1, 1) &&
    typeof msg.handbrake === 'boolean' &&
    typeof msg.boost === 'boolean'
  );
}

/** Upgrades are trusted from the server's copy of the save, never the wire. */
export interface RacerSetup {
  carId: string;
  color: string;
  upgrades: UpgradeLevels;
}
